import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BookOpen, Download, FolderOpen, Link2, Play, Plus, RefreshCw, Save } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { downloadDoc } from "@/lib/downloadDoc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Datev() {
  const { t, fmtDate } = useI18n();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [newAccount, setNewAccount] = useState({ account_number: "", name: "", account_type: "expense" });
  const [newTaxKey, setNewTaxKey] = useState({ code: "", name: "", rate: "19", direction: "output" });
  const [newCost, setNewCost] = useState({ kind: "cost_center", code: "", name: "" });
  const today = new Date();
  const [period, setPeriod] = useState({
    date_from: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10),
    date_to: new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10),
  });

  const { data: config } = useQuery({
    queryKey: ["datev-config"],
    queryFn: async () => { const d = (await api.get("/einvoice/datev/config")).data; setCfg(d); return d; },
  });
  const { data: batches } = useQuery({ queryKey: ["datev-batches"], queryFn: async () => (await api.get("/einvoice/datev/batches")).data });
  const { data: accounts = [] } = useQuery({ queryKey: ["datev-accounts"], queryFn: async () => (await api.get("/einvoice/datev/accounts")).data });
  const { data: taxKeys = [] } = useQuery({ queryKey: ["datev-tax-keys"], queryFn: async () => (await api.get("/einvoice/datev/tax-keys")).data });
  const { data: costObjects = [] } = useQuery({ queryKey: ["datev-cost-objects"], queryFn: async () => (await api.get("/einvoice/datev/cost-objects")).data });
  const { data: journal = { items: [] } } = useQuery({ queryKey: ["datev-journal"], queryFn: async () => (await api.get("/einvoice/datev/journal")).data });
  const { data: connection } = useQuery({ queryKey: ["datev-connection"], queryFn: async () => (await api.get("/einvoice/datev/connection")).data });

  const c = cfg || config || {};
  const set = (k, v) => setCfg({ ...c, [k]: v });

  const saveCfg = async () => {
    setBusy("save");
    try {
      await api.put("/einvoice/datev/config", { ...c, apply_chart_defaults: true });
      toast.success(t("einvoice.datev_saved"));
      qc.invalidateQueries({ queryKey: ["datev-config"] });
      qc.invalidateQueries({ queryKey: ["datev-accounts"] });
      qc.invalidateQueries({ queryKey: ["datev-tax-keys"] });
      setPreview(null);
    }
    catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusy(""); }
  };
  const doPreview = async () => {
    setBusy("preview");
    try { const { data } = await api.post("/einvoice/datev/preview", period); setPreview(data); if (!data.count) toast.info(t("einvoice.datev_nothing")); }
    catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusy(""); }
  };
  const doExport = async () => {
    setBusy("export");
    try {
      if (!preview || preview.errors?.length) {
        toast.error(t("einvoice.datev_preview_required"));
        return;
      }
      await api.post("/einvoice/datev/export", period);
      toast.success(t("einvoice.datev_exported")); setPreview(null); qc.invalidateQueries({ queryKey: ["datev-batches"] });
    }
    catch (e) {
      if (e?.response?.status === 409) toast.info(t("einvoice.datev_nothing"));
      else toast.error(apiErrorMessage(e, t));
    } finally { setBusy(""); }
  };

  const field = (k, label) => (
    <div><Label className="text-xs">{label}</Label>
      <Input value={c[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`datev-${k}`} /></div>
  );
  const createAccount = async () => {
    try {
      await api.post("/einvoice/datev/accounts", newAccount);
      setNewAccount({ account_number: "", name: "", account_type: "expense" });
      qc.invalidateQueries({ queryKey: ["datev-accounts"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const createCostObject = async () => {
    try {
      await api.post("/einvoice/datev/cost-objects", newCost);
      setNewCost({ kind: "cost_center", code: "", name: "" });
      qc.invalidateQueries({ queryKey: ["datev-cost-objects"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const createTaxKey = async () => {
    try {
      await api.post("/einvoice/datev/tax-keys", newTaxKey);
      setNewTaxKey({ code: "", name: "", rate: "19", direction: "output" });
      qc.invalidateQueries({ queryKey: ["datev-tax-keys"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const rebuildJournal = async () => {
    try {
      await api.post("/einvoice/datev/journal/rebuild");
      toast.success(t("einvoice.datev_journal_rebuilt"));
      qc.invalidateQueries({ queryKey: ["datev-journal"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const connectDatev = async () => {
    try {
      const { data } = await api.post("/einvoice/datev/connection/start");
      window.location.assign(data.authorization_url);
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const updatePosting = async (postingId, patch) => {
    try {
      await api.put(`/einvoice/datev/journal/${postingId}`, patch);
      qc.invalidateQueries({ queryKey: ["datev-journal"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };

  return (
    <div>
      <PageHeader title={t("einvoice.datev_title")} subtitle={t("einvoice.datev_subtitle")} />

      <Card className="mb-4 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("einvoice.datev_config")}</h3>
          <div className="flex items-center gap-2 text-sm">
            <span>{t("einvoice.datev_testmode")}</span>
            <Switch checked={!!c.test_mode} onCheckedChange={(v) => set("test_mode", v)} data-testid="datev-testmode" />
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {field("consultant_no", t("einvoice.datev_consultant"))}
          {field("client_no", t("einvoice.datev_client"))}
          <div><Label className="text-xs">{t("einvoice.datev_chart")}</Label><Select value={c.chart || "SKR03"} onValueChange={(value) => set("chart", value)}><SelectTrigger data-testid="datev-chart"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="SKR03">SKR03</SelectItem><SelectItem value="SKR04">SKR04</SelectItem><SelectItem value="custom">{t("einvoice.datev_custom_chart")}</SelectItem></SelectContent></Select></div>
          {field("revenue_19", t("einvoice.datev_rev19"))}
          {field("revenue_7", t("einvoice.datev_rev7"))}
          {field("debtor_account_start", t("einvoice.datev_debtor"))}
        </div>
        <p className={`mt-3 text-sm ${c.configured ? "text-emerald-600" : "text-amber-600"}`}>
          {c.configured ? t("einvoice.datev_ready") : t("einvoice.datev_not_ready")}
        </p>
        {can("datev:configure") && (
          <div className="mt-3 flex justify-end"><Button onClick={saveCfg} disabled={busy === "save"} data-testid="datev-save-config"><Save className="h-4 w-4" />{t("common.save")}</Button></div>
        )}
      </Card>

      <Card className="mb-4 p-4">
        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          <div><Label className="text-xs">{t("einvoice.datev_from")}</Label><Input type="date" value={period.date_from} onChange={(e) => { setPeriod((p) => ({ ...p, date_from: e.target.value })); setPreview(null); }} data-testid="datev-date-from" /></div>
          <div><Label className="text-xs">{t("einvoice.datev_to")}</Label><Input type="date" value={period.date_to} onChange={(e) => { setPeriod((p) => ({ ...p, date_to: e.target.value })); setPreview(null); }} data-testid="datev-date-to" /></div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("einvoice.datev_generate")}</h3>
            <p className="mt-1 text-sm text-slate-500">{t("einvoice.datev_generate_desc")}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={doPreview} disabled={busy === "preview"} data-testid="datev-preview-button"><Play className="h-4 w-4" />{t("einvoice.datev_preview")}</Button>
            <Button onClick={doExport} disabled={busy === "export" || !preview || preview.errors?.length > 0 || preview.count === 0} data-testid="datev-export-button"><Download className="h-4 w-4" />{t("einvoice.datev_export")}</Button>
          </div>
        </div>
        {preview && (
          <div className="mt-3 rounded-md bg-slate-50 p-3 text-sm dark:bg-slate-800/50" data-testid="datev-preview-result">
            <p>{t("einvoice.datev_invoices")}: <b>{preview.count}</b> · {t("einvoice.datev_rows")}: <b>{preview.row_count}</b> · {t("einvoice.datev_control")}: <b className="tabular-nums">{preview.control_sum} €</b></p>
            {preview.errors?.length > 0 && <div className="mt-2 rounded-md bg-red-50 p-2 text-red-700 dark:bg-red-950/40 dark:text-red-300">
              <p>{preview.errors.length} {t("einvoice.datev_errors")}</p>
              <ul className="mt-1 list-inside list-disc text-xs">{preview.errors.map((error, index) => <li key={`${error.code}-${index}`}>{t(`einvoice.datev_error_${error.code}`)}{error.invoice ? ` · ${error.invoice}` : ""}</li>)}</ul>
            </div>}
            <p className="mt-1 text-xs text-slate-400">{preview.disclaimer}</p>
          </div>
        )}
      </Card>

      <div className="mb-4 grid gap-4 xl:grid-cols-2">
        <Card className="p-4">
          <div className="mb-3 flex items-center justify-between"><h3 className="flex items-center gap-2 font-semibold"><BookOpen className="h-4 w-4" />{t("einvoice.datev_accounts")}</h3><span className="text-xs text-slate-500">{accounts.length}</span></div>
          <div className="max-h-72 overflow-auto rounded-md border">
            <Table><TableHeader><TableRow><TableHead>{t("einvoice.datev_account")}</TableHead><TableHead>{t("common.name")}</TableHead><TableHead>{t("einvoice.datev_account_type")}</TableHead><TableHead>{t("einvoice.datev_tax_key")}</TableHead></TableRow></TableHeader>
              <TableBody>{accounts.map((account) => <TableRow key={account.id}><TableCell className="font-mono">{account.account_number}</TableCell><TableCell>{account.name}</TableCell><TableCell>{t(`einvoice.datev_type_${account.account_type}`)}</TableCell><TableCell>{account.default_tax_key || "—"}</TableCell></TableRow>)}</TableBody>
            </Table>
          </div>
          {can("datev:configure") && <div className="mt-3 grid gap-2 sm:grid-cols-[100px_1fr_150px_auto]">
            <Input inputMode="numeric" placeholder={t("einvoice.datev_account")} value={newAccount.account_number} onChange={(e) => setNewAccount((x) => ({ ...x, account_number: e.target.value.replace(/\D/g, "") }))} />
            <Input placeholder={t("common.name")} value={newAccount.name} onChange={(e) => setNewAccount((x) => ({ ...x, name: e.target.value }))} />
            <Select value={newAccount.account_type} onValueChange={(account_type) => setNewAccount((x) => ({ ...x, account_type }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["asset", "liability", "revenue", "expense", "debtor", "creditor"].map((type) => <SelectItem key={type} value={type}>{t(`einvoice.datev_type_${type}`)}</SelectItem>)}</SelectContent></Select>
            <Button onClick={createAccount} disabled={!newAccount.account_number || !newAccount.name}><Plus className="h-4 w-4" /></Button>
          </div>}
        </Card>

        <Card className="p-4">
          <h3 className="mb-3 font-semibold">{t("einvoice.datev_tax_keys")}</h3>
          <div className="rounded-md border"><Table><TableHeader><TableRow><TableHead>{t("einvoice.datev_tax_key")}</TableHead><TableHead>{t("common.name")}</TableHead><TableHead className="text-right">{t("einvoice.datev_tax_rate")}</TableHead><TableHead>{t("einvoice.datev_direction")}</TableHead></TableRow></TableHeader>
            <TableBody>{taxKeys.map((key) => <TableRow key={key.id}><TableCell className="font-mono">{key.code}</TableCell><TableCell>{key.name}</TableCell><TableCell className="text-right">{key.rate}%</TableCell><TableCell>{t(`einvoice.datev_direction_${key.direction}`)}</TableCell></TableRow>)}</TableBody>
          </Table></div>
          {can("datev:configure") && <div className="mt-3 grid gap-2 sm:grid-cols-[80px_1fr_90px_130px_auto]">
            <Input placeholder={t("einvoice.datev_tax_key")} value={newTaxKey.code} onChange={(e) => setNewTaxKey((x) => ({ ...x, code: e.target.value }))} />
            <Input placeholder={t("common.name")} value={newTaxKey.name} onChange={(e) => setNewTaxKey((x) => ({ ...x, name: e.target.value }))} />
            <Input type="number" min="0" step="0.01" placeholder="%" value={newTaxKey.rate} onChange={(e) => setNewTaxKey((x) => ({ ...x, rate: e.target.value }))} />
            <Select value={newTaxKey.direction} onValueChange={(direction) => setNewTaxKey((x) => ({ ...x, direction }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["output", "input", "neutral"].map((direction) => <SelectItem key={direction} value={direction}>{t(`einvoice.datev_direction_${direction}`)}</SelectItem>)}</SelectContent></Select>
            <Button onClick={createTaxKey} disabled={!newTaxKey.code || !newTaxKey.name}><Plus className="h-4 w-4" /></Button>
          </div>}
          <h3 className="mb-3 mt-5 font-semibold">{t("einvoice.datev_cost_accounting")}</h3>
          <div className="space-y-2">{costObjects.map((item) => <div key={item.id} className="flex items-center justify-between rounded-md border p-2 text-sm"><span><b className="font-mono">{item.code}</b> · {item.name}</span><span className="text-xs text-slate-500">{t(`einvoice.datev_${item.kind}`)}</span></div>)}</div>
          {can("datev:configure") && <div className="mt-3 grid gap-2 sm:grid-cols-[150px_110px_1fr_auto]">
            <Select value={newCost.kind} onValueChange={(kind) => setNewCost((x) => ({ ...x, kind }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cost_center">{t("einvoice.datev_cost_center")}</SelectItem><SelectItem value="cost_unit">{t("einvoice.datev_cost_unit")}</SelectItem></SelectContent></Select>
            <Input placeholder={t("einvoice.datev_code")} value={newCost.code} onChange={(e) => setNewCost((x) => ({ ...x, code: e.target.value }))} />
            <Input placeholder={t("common.name")} value={newCost.name} onChange={(e) => setNewCost((x) => ({ ...x, name: e.target.value }))} />
            <Button onClick={createCostObject} disabled={!newCost.code || !newCost.name}><Plus className="h-4 w-4" /></Button>
          </div>}
        </Card>
      </div>

      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h3 className="flex items-center gap-2 font-semibold"><Link2 className="h-4 w-4" />DATEV Unternehmen online</h3><p className="mt-1 text-sm text-slate-500">{t("einvoice.datev_connection_desc")}</p></div>
          <div className="flex items-center gap-2"><span className={`rounded-full px-2 py-1 text-xs ${connection?.status === "connected" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{t(`einvoice.datev_connection_${connection?.status || "not_configured"}`)}</span>{can("datev:configure") && <Button onClick={connectDatev} disabled={!connection?.client_configured}><Link2 className="h-4 w-4" />{t("einvoice.datev_connect")}</Button>}</div>
        </div>
        {!connection?.client_configured && <p className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">{t("einvoice.datev_client_missing")}</p>}
      </Card>

      <Card className="mb-4">
        <div className="flex items-center justify-between border-b p-4"><div><h3 className="font-semibold">{t("einvoice.datev_journal")}</h3><p className="text-sm text-slate-500">{t("einvoice.datev_journal_desc")}</p></div>{can("datev:configure") && <Button variant="outline" onClick={rebuildJournal}><RefreshCw className="h-4 w-4" />{t("einvoice.datev_rebuild")}</Button>}</div>
        {journal.items.length === 0 ? <EmptyState icon={BookOpen} title={t("einvoice.datev_no_postings")} /> : <div className="max-h-96 overflow-auto"><Table><TableHeader><TableRow><TableHead>{t("einvoice.date")}</TableHead><TableHead>{t("einvoice.datev_document")}</TableHead><TableHead>{t("einvoice.datev_account")}</TableHead><TableHead>{t("einvoice.datev_contra_account")}</TableHead><TableHead>{t("einvoice.datev_tax_key")}</TableHead><TableHead>{t("einvoice.datev_cost_center")}</TableHead><TableHead>{t("einvoice.datev_cost_unit")}</TableHead><TableHead className="text-right">{t("einvoice.datev_amount")}</TableHead><TableHead>{t("common.status")}</TableHead></TableRow></TableHeader>
          <TableBody>{journal.items.map((posting) => <TableRow key={posting.id}><TableCell>{fmtDate(posting.booking_date)}</TableCell><TableCell>{posting.source_number || "—"}</TableCell><TableCell className="font-mono">{posting.account}</TableCell><TableCell className="font-mono">{posting.contra_account}</TableCell><TableCell>{posting.tax_key || "—"}</TableCell>
            <TableCell>{posting.status === "open" && can("datev:configure") ? <Select value={posting.cost_center || "none"} onValueChange={(value) => updatePosting(posting.id, { cost_center: value === "none" ? null : value })}><SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">—</SelectItem>{costObjects.filter((x) => x.kind === "cost_center").map((x) => <SelectItem key={x.id} value={x.code}>{x.code}</SelectItem>)}</SelectContent></Select> : posting.cost_center || "—"}</TableCell>
            <TableCell>{posting.status === "open" && can("datev:configure") ? <Select value={posting.cost_unit || "none"} onValueChange={(value) => updatePosting(posting.id, { cost_unit: value === "none" ? null : value })}><SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">—</SelectItem>{costObjects.filter((x) => x.kind === "cost_unit").map((x) => <SelectItem key={x.id} value={x.code}>{x.code}</SelectItem>)}</SelectContent></Select> : posting.cost_unit || "—"}</TableCell>
            <TableCell className="text-right tabular-nums">{posting.amount} {posting.currency}</TableCell><TableCell>{posting.status}</TableCell></TableRow>)}</TableBody>
        </Table></div>}
      </Card>

      <Card>
        <div className="border-b border-slate-200 p-4 dark:border-slate-800"><h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("einvoice.datev_batches")}</h3></div>
        {!batches || batches.length === 0 ? (
          <EmptyState icon={FolderOpen} title={t("einvoice.datev_no_batches")} testId="datev-empty" />
        ) : (
          <Table data-testid="datev-batches-table">
            <TableHeader><TableRow>
              <TableHead>{t("einvoice.date")}</TableHead><TableHead className="text-right">{t("einvoice.datev_invoices")}</TableHead>
              <TableHead className="text-right">{t("einvoice.datev_rows")}</TableHead><TableHead className="text-right">{t("einvoice.datev_control")}</TableHead>
              <TableHead>{t("einvoice.datev_mode")}</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {batches.map((b) => (
                <TableRow key={b.id} data-testid={`datev-batch-${b.id}`}>
                  <TableCell>{b.created_at ? fmtDate(b.created_at) : "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{b.invoice_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{b.row_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{b.control_sum} €</TableCell>
                  <TableCell>{b.test_mode ? t("einvoice.datev_test") : t("einvoice.datev_live")}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => downloadDoc(`/einvoice/datev/batches/${b.id}/download`, `DATEV_${b.id.slice(0, 8)}.zip`)} data-testid={`datev-download-${b.id}`}><Download className="h-4 w-4" />ZIP</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
